import { Canvas, unmountComponentAtNode } from "@react-three/fiber";
import type { GLProps } from "@react-three/fiber";
import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { SRGBColorSpace, WebGLRenderer } from "three";
import {
  DEFAULT_CAMERA_POSITION,
  DEFAULT_CAMERA_VERTICAL_FOV_DEGREES,
} from "./cameraComposition";
import { SystemScene } from "./SystemScene";

const EARTH_VIEW_SLOT_SELECTOR = ".earth-view-slot";

export type SceneQuality = "full" | "reduced";

interface NavigatorPerformanceHints extends Navigator {
  readonly deviceMemory?: number;
  readonly connection?: { readonly saveData?: boolean };
}

function detectSceneQuality(): SceneQuality {
  if (typeof navigator === "undefined") {
    return "reduced";
  }

  const hints = navigator as NavigatorPerformanceHints;
  const requestsReducedData =
    hints.connection?.saveData === true ||
    window.matchMedia?.("(prefers-reduced-data: reduce)").matches === true;
  const hasLowMemory =
    hints.deviceMemory !== undefined && hints.deviceMemory < 4;
  const hasFewCores =
    hints.hardwareConcurrency !== undefined && hints.hardwareConcurrency < 4;

  return requestsReducedData || hasLowMemory || hasFewCores
    ? "reduced"
    : "full";
}

function hasWebGl2Api(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.WebGL2RenderingContext !== "undefined"
  );
}

export function SceneCanvas({
  onFallbackNeeded,
  onQualityChange,
  tourStep = null,
}: {
  readonly onFallbackNeeded?: () => void;
  readonly onQualityChange?: (quality: SceneQuality) => void;
  readonly tourStep?: number | null;
}) {
  const hasWebGl2 = useMemo(() => hasWebGl2Api(), []);
  const quality = useMemo(() => detectSceneQuality(), []);
  const [canvasFailed, setCanvasFailed] = useState(false);
  const fallbackNeeded = useRef(onFallbackNeeded);
  const failureReported = useRef(false);
  const qualityReported = useRef(false);
  const removeContextLossListener = useRef<() => void>(() => {});

  const reportFailure = useCallback(() => {
    if (failureReported.current) {
      return;
    }
    failureReported.current = true;
    setCanvasFailed(true);
    fallbackNeeded.current?.();
  }, []);
  const createRenderer: GLProps = useCallback(
    async (defaults) => {
      try {
        return new WebGLRenderer({
          ...defaults,
          alpha: false,
          antialias: quality === "full",
          powerPreference:
            quality === "full" ? "high-performance" : "low-power",
        });
      } catch {
        try {
          unmountComponentAtNode(defaults.canvas);
        } catch {
          // App-level unmount below remains the final cleanup boundary.
        }
        reportFailure();

        // R3F 9.7 does not observe configure() rejections. Keeping configure
        // pending prevents an unhandled rejection and stops it before render().
        // The explicit unmount stops the internal root even if Canvas has not
        // installed its passive cleanup yet. No timer, listener, or module
        // state retains this promise, so the abandoned await chain remains
        // collectible after R3F removes its canvas/root bookkeeping.
        return new Promise<WebGLRenderer>(() => undefined);
      }
    },
    [quality, reportFailure],
  );
  const observeContextLoss = useCallback(
    (canvas: HTMLCanvasElement) => {
      removeContextLossListener.current();

      const onContextLost = (event: Event) => {
        event.preventDefault();
        removeContextLossListener.current();
        removeContextLossListener.current = () => {};
        reportFailure();
      };

      canvas.addEventListener("webglcontextlost", onContextLost, {
        once: true,
      });
      removeContextLossListener.current = () => {
        canvas.removeEventListener("webglcontextlost", onContextLost);
      };
    },
    [reportFailure],
  );
  const reportQuality = useCallback(() => {
    if (qualityReported.current) {
      return;
    }
    qualityReported.current = true;
    onQualityChange?.(quality);
  }, [onQualityChange, quality]);
  const handleCreated = useCallback(
    ({ gl }: { readonly gl: WebGLRenderer }) => {
      try {
        if (gl.getContext().isContextLost()) {
          throw new Error("WebGL context was lost during scene creation");
        }
        gl.outputColorSpace = SRGBColorSpace;
        observeContextLoss(gl.domElement);
        reportQuality();
      } catch {
        reportFailure();
      }
    },
    [observeContextLoss, reportFailure, reportQuality],
  );

  useEffect(() => {
    fallbackNeeded.current = onFallbackNeeded;
  }, [onFallbackNeeded]);

  useEffect(() => {
    if (!hasWebGl2) {
      reportFailure();
    }
  }, [hasWebGl2, reportFailure]);

  useEffect(
    () => () => {
      removeContextLossListener.current();
    },
    [],
  );

  if (!hasWebGl2 || canvasFailed) {
    return null;
  }

  return (
    <Canvas
      aria-label="Interactive Earth, Moon, and sunlight geometry"
      camera={{
        far: 100,
        fov: DEFAULT_CAMERA_VERTICAL_FOV_DEGREES,
        near: 0.1,
        position: DEFAULT_CAMERA_POSITION,
      }}
      className="scene-canvas"
      dpr={quality === "full" ? [1, 1.5] : 1}
      gl={createRenderer}
      onCreated={handleCreated}
    >
      <Suspense fallback={null}>
        <SystemScene
          earthViewTargetSelector={EARTH_VIEW_SLOT_SELECTOR}
          quality={quality}
          tourStep={tourStep}
        />
      </Suspense>
    </Canvas>
  );
}
